import { routes } from "../../lib/nav";
import { SITE_URL } from "../../lib/site";
import type { CaseStudy } from "./types";

const dir = "/cases/petsroom";

const img = (file: string, alt: string, span: 2 | 3 | 6 = 6) => ({
  src: `${dir}/${file}.webp`,
  alt,
  width: 1920,
  height: 1200,
  wide: span === 6,
  span,
});

const mobile = (file: string, alt: string) => ({
  src: `${dir}/${file}.webp`,
  alt,
  width: 1920,
  height: 1200,
  span: 3 as const,
});

export const petsroom: CaseStudy = {
  slug: "petsroom",
  title: "PetsRoom",
  cardTitle: "Разработка и продвижение интернет-магазина зоотоваров PetsRoom",
  subtitle: "Интернет-магазин зоотоваров",
  industry: { label: "Зоотовары", slug: "pets" },
  eyebrow: "Интернет-магазин · Зоотовары",
  h1: "Разработка и продвижение интернет-магазина PetsRoom",
  lead:
    "Разработали интернет-магазин зоотоваров PetsRoom: каталог по видам животных, брендам и категориям, сравнение, избранное, личный кабинет с бонусами и связанное груминг-направление — и ведём продвижение проекта.",
  heroButtons: { site: "Смотреть сайт", discuss: "Обсудить интернет-магазин" },

  seo: {
    primaryKeyword: "разработка и продвижение интернет-магазина зоотоваров",
    secondaryKeywords: [
      "интернет-магазин зоотоваров разработка",
      "e-commerce для pet retail",
      "сайт зоомагазина",
      "продвижение зоомагазина",
    ],
    semanticEntities: [
      "каталог зоотоваров",
      "товары для собак",
      "товары для кошек",
      "сравнение товаров",
      "избранное",
      "личный кабинет",
      "бонусы и скидки",
      "груминг",
    ],
    excludedKeywords: [
      "корм для кошек купить",
      "зоомагазин Химки",
      "игрушки для собак",
      "груминг цена",
    ],
    title: "Интернет-магазин PetsRoom — кейс HAY.agency",
    description:
      "Кейс HAY.agency: интернет-зоомагазин PetsRoom с каталогом по видам животных, брендами, акциями, контентом и отдельным груминг-направлением.",
    h1: "Разработка и продвижение интернет-магазина PetsRoom",
    slug: "petsroom",
    canonicalUrl: `${SITE_URL}/cases/petsroom`,
    ogTitle: "Интернет-магазин зоотоваров PetsRoom",
    ogDescription:
      "Каталог по видам животных, сравнение, избранное, личный кабинет с бонусами и связанное груминг-направление.",
    ogImage: `${SITE_URL}${dir}/petsroom-og.webp`,
    robotsIndex: true,
    datePublished: "2024-02-01",
    dateModified: "2026-09-01",
  },

  links: {
    relatedService: {
      label: "Разработка и развитие интернет-магазинов",
      to: routes.ecom,
      note: "Каталог, карточка товара и сценарии покупки",
    },
    relatedIndustry: {
      label: "SEO-продвижение интернет-магазинов",
      to: routes.seo,
      note: "Видимость каталога и категорий",
    },
    relatedCases: [
      {
        label: "Интернет-магазин профессиональной косметики Quantum Shop",
        to: "/cases/quantum-shop",
        note: "Мультибрендовый каталог и e-commerce сценарии",
      },
      {
        label: "Разработка интернет-магазина цветов «Цветочный маркет»",
        to: "/cases/cvmarket",
        note: "Каталог, карточка товара и корзина",
      },
    ],
    relatedArticles: [],
  },

  cta: {
    title: "Нужен интернет-магазин зоотоваров, который продаёт?",
    text: "Спроектируем каталог по видам животных, лояльность и корзину, возьмём продвижение на себя.",
    button: "Обсудить интернет-магазин",
  },

  client: {
    name: "PetsRoom",
    url: "https://petsroom.com/",
    city: "Россия",
    industry: "Зоотовары",
  },
  year: "С 2024 года",
  services: [
    "Дизайн интернет-магазина",
    "Разработка каталога по видам животных",
    "Карточка товара и корзина",
    "Личный кабинет и бонусная программа",
    "SEO-продвижение",
    "Сопровождение проекта",
  ],
  platform: "1С-Битрикс",
  tags: [
    { label: "Интернет-магазин", to: routes.ecom },
    { label: "Разработка", to: routes.ecom },
    { label: "SEO", to: routes.seo },
    { label: "Поддержка", to: routes.ecom },
  ],
  facts: [
    { label: "Клиент", value: "PetsRoom" },
    { label: "Отрасль", value: "Зоотовары" },
    { label: "Тип проекта", value: "Интернет-магазин зоотоваров" },
    { label: "Платформа", value: "1С-Битрикс" },
  ],

  results: {
    title: "Что реализовали",
    items: [
      "Каталог зоотоваров по видам животных: собаки, кошки, грызуны, птицы и рыбы",
      "Сравнение товаров и избранное",
      "Личный кабинет с бонусами и скидками",
      "Блог и мобильная нижняя навигация",
      "Связанное груминг-направление",
      "SEO-продвижение интернет-магазина",
    ],
  },

  story: [
    {
      title: "Задача",
      paragraphs: [
        "PetsRoom продаёт товары сразу для нескольких видов животных — от собак и кошек до грызунов, птиц и рыб. Каталогу с таким разбросом категорий нужна была понятная точка входа по питомцу, а не единый общий список товаров.",
        "Вторая часть задачи — встроить в магазин связанное груминг-направление и повторные покупки: бонусы, скидки и блог, — не превращая интернет-магазин в набор разрозненных разделов.",
      ],
    },
  ],
  features: [],

  design: {
    title: "Дизайн: светлый интерфейс с умеренным pet-акцентом",
    paragraphs: [
      "Интерфейс собран на светлом warm-grey фоне — товары и карточки читаются на первом плане, а pet-акцент остаётся умеренным дополнением, а не главным элементом.",
      "Каталог и карточка товара выстроены под навигацию по виду животного, чтобы покупатель быстро попадал в свою категорию.",
    ],
    bullets: [
      "Светлый warm-grey фон",
      "Реальные товары в приоритете подачи",
      "Умеренный pet-акцент без перегруза интерфейса",
    ],
  },

  identity: {
    fonts: [
      { name: "Open Sans", role: "Заголовки", sample: "Каталог PetsRoom" },
      { name: "Golos Text", role: "Интерфейс и описания", sample: "Товары для собак" },
    ],
    colors: [
      { hex: "#F4F2EF", name: "Фон витрины" },
      { hex: "#E4A94B", name: "Акцент бренда" },
      { hex: "#1D1C1A", name: "Текст" },
    ],
  },

  cover: img(
    "petsroom-case-cover",
    "Главная страница интернет-магазина зоотоваров PetsRoom",
    6,
  ),

  gallery: [
    img("petsroom-catalog", "Каталог зоотоваров по видам животных на сайте PetsRoom", 6),
  ],

  responsive: {
    text: [
      "Мобильная версия повторяет логику десктопа: выбор питомца, категория, карточка товара и корзина в один сценарий.",
      "Нижняя навигация на мобильном даёт быстрый доступ к каталогу, избранному, корзине и личному кабинету.",
    ],
    images: [
      mobile("petsroom-mobile-hero", "Мобильная главная интернет-магазина PetsRoom"),
      mobile("petsroom-mobile-catalog", "Мобильный каталог зоотоваров PetsRoom"),
    ],
  },

  columnBlocks: [
    {
      id: "catalog",
      title: "Каталог по питомцу",
      paragraphs: [
        "Вход в каталог построен по виду животного, категориям и брендам — покупатель сразу попадает к товарам для своего питомца.",
      ],
      columns: [
        { title: "Виды животных", items: ["Собаки", "Кошки", "Грызуны, птицы и рыбы"] },
        { title: "Навигация", items: ["По категориям", "По брендам"] },
      ],
    },
    {
      id: "ecommerce",
      title: "E-commerce функции",
      paragraphs: [
        "Сравнение, избранное, личный кабинет и заполненная корзина — стандартный набор сценариев для повторных покупок в зоомагазине.",
      ],
      bullets: ["Сравнение товаров", "Избранное", "Личный кабинет с бонусами и скидками"],
    },
    {
      id: "content",
      title: "Контент и повторные покупки",
      paragraphs: [
        "Блог, бонусы и скидки собраны в одной композиции, которая мотивирует покупателя возвращаться в магазин за повторными заказами.",
      ],
      bullets: ["Блог с материалами о питомцах", "Бонусы и скидки"],
    },
    {
      id: "grooming",
      title: "Связанная услуга",
      paragraphs: [
        "Груминг-направление встроено в структуру магазина как связанная услуга, дополняющая продажу товаров.",
      ],
      bullets: ["Груминг-направление в общей структуре сайта"],
    },
  ],

  servicesDone: {
    title: "Комплекс работ HAY.agency",
    items: [
      { label: "Разработка интернет-магазина", to: routes.ecom },
      { label: "SEO-продвижение", to: routes.seo },
      { label: "Поддержка и развитие", to: routes.ecom },
    ],
  },
};
